import React, { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  updateDoc
} from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { useAuth } from '../../../contexts/AuthContext';
import {
  ShoppingBag,
  Package,
  Truck,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  Search,
  Filter,
  Eye,
  IndianRupee,
  Calendar,
  ChevronRight,
  ChevronDown,
  RefreshCw,
  FileText,
  Building,
  ShoppingCart,
  ArrowLeft,
  Hash,
  BadgeCheck,
  MapPin,
  Briefcase,
  ExternalLink,
  Palette // NEW: Import Palette icon
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const MyOrdersPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Status configurations
  const statusConfig = {
    pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
    confirmed: { label: 'Confirmed', color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
    processing: { label: 'Processing', color: 'bg-purple-100 text-purple-800', icon: RefreshCw },
    shipped: { label: 'Shipped', color: 'bg-indigo-100 text-indigo-800', icon: Truck },
    delivered: { label: 'Delivered', color: 'bg-green-100 text-green-800', icon: CheckCircle },
    cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-800', icon: XCircle }
  };

  // Format INR currency
  const formatINR = (amount) => {
    if (!amount) return '₹0';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Format date
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Function to fetch user contact info from users collection
  const fetchUserContactInfo = async (userId) => {
    try {
      if (!userId) return null;
      
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const userData = userSnap.data();
        
        return {
          name: userData.name || userData.displayName || userData.contactPerson || '',
          email: userData.email || userData.contactEmail || userData.sellerEmail || '',
          phone: userData.phone || userData.mobile || userData.contactPhone || '',
          company: userData.companyName || userData.company || '',
          address: userData.address || userData.companyAddress || '',
          city: userData.city || userData.companyCity || '',
          state: userData.state || userData.companyState || '',
          country: userData.country || userData.companyCountry || 'India',
          pincode: userData.postalCode || userData.pincode || userData.companyPincode || '',
          gst: userData.GSTNumber || userData.gstNumber || userData.gst || '',
          pan: userData.PAN || userData.panNumber || userData.pan || '',
          website: userData.website || userData.companyWebsite || ''
        };
      } else {
        return null;
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      return null;
    }
  };

  // Function to parse colors from string to array
  const parseColors = (colorsString) => {
    if (!colorsString || colorsString.trim() === '') return [];
    
    // Split by common separators: comma, plus, ampersand, pipe, slash
    const colors = colorsString
      .split(/[,+&|/]/)
      .map(color => color.trim())
      .filter(color => color.length > 0);
    
    return colors;
  };

  // Fetch orders from saved-products collection
  const fetchOrders = async () => {
    try {
      setLoading(true);
      setIsRefreshing(true);
      
      if (!user) {
        toast.error('Please login to view orders');
        return;
      }

      const savedProductsRef = collection(db, 'saved-products');
      
      // Fetch from saved-products
      const q = query(
        savedProductsRef,
        where('buyerId', '==', user.uid)
      );

      const querySnapshot = await getDocs(q);
      const ordersList = [];

      // Process orders in parallel for better performance
      const orderPromises = querySnapshot.docs.map(async (docSnap) => {
        const data = docSnap.data();
        
        // Parse colors if they exist
        const productColors = data.productColors || '';
        const selectedColor = data.selectedColor || '';
        const availableColors = parseColors(productColors);
        
        // Build basic order object
        const orderData = {
          id: docSnap.id,
          ...data,
          
          // Product Information
          productId: data.productId || '',
          productName: data.productName || data.name || 'Unnamed Product',
          productImage: data.productImage || data.image || '',
          productPrice: data.productPrice || data.price || 0,
          productUnit: data.productUnit || data.unit || 'PCS',
          
          // Color Information - NEW
          productColors: productColors,
          selectedColor: selectedColor,
          availableColors: availableColors,
          
          // Order Information
          orderId: data.orderId || docSnap.id,
          quantity: data.quantity || 1,
          unitPrice: data.unitPrice || data.price || 0,
          totalAmount: data.totalAmount || data.totalPrice || 0,
          moq: data.moq || 1,
          orderStatus: data.orderStatus || data.status || 'confirmed',
          paymentStatus: data.paymentStatus || 'pending',
          orderType: data.orderType || 'direct_order',
          
          // Product Details
          category: data.category || '',
          material: data.material || '',
          standard: data.standard || '',
          origin: data.origin || '',
          warranty: data.warranty || '',
          leadTime: data.leadTime || '',
          shippingTerms: data.shippingTerms || '',
          paymentTerms: data.paymentTerms || '',
          packaging: data.packaging || '',
          description: data.description || '',
          
          // Seller Information
          sellerId: data.sellerId || '',
          sellerName: data.sellerName || data.companyName || 'Unknown Seller',
          
          // Contact Information
          sellerPhone: data.sellerPhone || data.phone || data.mobile || data.contactPhone || '',
          sellerEmail: data.sellerEmail || data.email || data.contactEmail || '',
          sellerWhatsapp: data.sellerWhatsapp || data.whatsapp || data.whatsappNumber || '',
          sellerAlternatePhone: data.sellerAlternatePhone || data.alternatePhone || '',
          sellerContactPerson: data.sellerContactPerson || data.contactPerson || '',
          
          // Company info
          sellerCompany: data.sellerCompany || data.companyName || data.sellerName || '',
          sellerAddress: data.sellerAddress || data.address || '',
          sellerCity: data.sellerCity || data.city || '',
          sellerState: data.sellerState || data.state || '',
          sellerCountry: data.sellerCountry || data.country || 'India',
          sellerPincode: data.sellerPincode || data.pincode || '',
          sellerGST: data.sellerGST || data.gst || '',
          sellerWebsite: data.sellerWebsite || data.website || '',
          
          // Response info
          sellerResponseTime: data.sellerResponseTime || 'Within 24 hours',
          sellerResponseRate: data.sellerResponseRate || '95%',
          sellerRating: data.sellerRating || 4.8,
          sellerVerified: data.sellerVerified || false,
          
          // Buyer info
          buyerId: data.buyerId || user.uid,
          buyerName: data.buyerName || '',
          buyerEmail: data.buyerEmail || '',
          buyerPhone: data.buyerPhone || '',
          
          // Timestamps
          createdAt: data.createdAt || data.orderDate || new Date(),
          updatedAt: data.updatedAt || new Date(),
        };

        // Fetch seller contact info only if missing
        if ((!orderData.sellerPhone || !orderData.sellerEmail) && orderData.sellerId) {
          try {
            const sellerContactInfo = await fetchUserContactInfo(orderData.sellerId);
            
            if (sellerContactInfo) {
              orderData.sellerPhone = sellerContactInfo.phone || orderData.sellerPhone || '';
              orderData.sellerEmail = sellerContactInfo.email || orderData.sellerEmail || '';
              orderData.sellerContactPerson = sellerContactInfo.name || orderData.sellerContactPerson || '';
              orderData.sellerCompany = sellerContactInfo.company || orderData.sellerCompany || '';
              orderData.sellerAddress = sellerContactInfo.address || orderData.sellerAddress || '';
              orderData.sellerCity = sellerContactInfo.city || orderData.sellerCity || '';
              orderData.sellerState = sellerContactInfo.state || orderData.sellerState || '';
              orderData.sellerCountry = sellerContactInfo.country || orderData.sellerCountry || 'India';
              orderData.sellerPincode = sellerContactInfo.pincode || orderData.sellerPincode || '';
              orderData.sellerGST = sellerContactInfo.gst || orderData.sellerGST || '';
              orderData.sellerWebsite = sellerContactInfo.website || orderData.sellerWebsite || '';
            }
          } catch (error) {
            console.error('Error fetching seller contact info:', error);
          }
        }

        // Fetch buyer contact info only if missing
        if ((!orderData.buyerPhone || !orderData.buyerEmail) && orderData.buyerId) {
          try {
            const buyerContactInfo = await fetchUserContactInfo(orderData.buyerId);
            
            if (buyerContactInfo) {
              orderData.buyerName = buyerContactInfo.name || orderData.buyerName || '';
              orderData.buyerEmail = buyerContactInfo.email || orderData.buyerEmail || '';
              orderData.buyerPhone = buyerContactInfo.phone || orderData.buyerPhone || '';
            }
          } catch (error) {
            console.error('Error fetching buyer contact info:', error);
          }
        }
        
        return orderData;
      });

      // Wait for all promises to resolve
      const resolvedOrders = await Promise.all(orderPromises);
      ordersList.push(...resolvedOrders);

      // Sort by createdAt DESCENDING
      ordersList.sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 
                     new Date(a.createdAt).getTime();
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 
                     new Date(b.createdAt).getTime();
        return dateB - dateA;
      });

      setOrders(ordersList);

    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to load orders. Please try again.');
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  // Handle order action
  const handleOrderAction = async (orderId, action) => {
    try {
      const orderRef = doc(db, 'saved-products', orderId);
      
      switch (action) {
        case 'cancel':
          await updateDoc(orderRef, {
            orderStatus: 'cancelled',
            updatedAt: new Date()
          });
          toast.success('Order cancelled successfully');
          break;
        
        default:
          break;
      }

      fetchOrders();
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error('Failed to update order');
    }
  };

  // Filter orders based on search and status
  const filteredOrders = orders.filter(order => {
    const matchesSearch = 
      (order.productName?.toLowerCase().includes(searchTerm.toLowerCase()) || false) ||
      (order.orderId?.toLowerCase().includes(searchTerm.toLowerCase()) || false) ||
      (order.sellerName?.toLowerCase().includes(searchTerm.toLowerCase()) || false) ||
      (order.sellerCompany?.toLowerCase().includes(searchTerm.toLowerCase()) || false) ||
      (order.selectedColor?.toLowerCase().includes(searchTerm.toLowerCase()) || false) || // NEW: Search by color
      (order.productColors?.toLowerCase().includes(searchTerm.toLowerCase()) || false); // NEW: Search by all colors
    
    const matchesStatus = statusFilter === 'all' || order.orderStatus === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Toggle order details
  const toggleOrderDetails = (orderId) => {
    setExpandedOrder(expandedOrder === orderId ? null : orderId);
  };

  // Fetch orders on component mount
  useEffect(() => {
    if (user) {
      fetchOrders();
    }
  }, [user]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-gray-300 rounded w-48"></div>
            <div className="h-10 bg-gray-300 rounded"></div>
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-32 bg-gray-300 rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <div className="flex items-center mb-2">
                <button
                  onClick={() => navigate('/buyer/dashboard')}
                  className="mr-4 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <ArrowLeft className="h-5 w-5 text-gray-600" />
                </button>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center">
                  <ShoppingBag className="h-8 w-8 mr-3 text-orange-600" />
                  My Orders ({orders.length})
                </h1>
              </div>
              <p className="text-gray-600 ml-14">
                Track and manage all your orders in one place
              </p>
            </div>
            <button
              onClick={fetchOrders}
              disabled={isRefreshing}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
              {isRefreshing ? 'Refreshing...' : 'Refresh Orders'}
            </button>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-8">
          <div className="p-6">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search orders by product, order ID, seller, or color..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                  />
                </div>
              </div>

              {/* Status Filter */}
              <div className="w-full md:w-64">
                <div className="relative">
                  <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 appearance-none bg-white transition-colors"
                  >
                    <option value="all">All Orders</option>
                    <option value="pending">Pending</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="processing">Processing</option>
                    <option value="shipped">Shipped</option>
                    <option value="delivered">Delivered</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Orders List */}
        {filteredOrders.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Package className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Orders Found</h3>
            <p className="text-gray-600 mb-6">
              {searchTerm || statusFilter !== 'all' 
                ? 'Try adjusting your search or filter criteria'
                : 'You haven\'t placed any orders yet'}
            </p>
            {!searchTerm && statusFilter === 'all' && (
              <button
                onClick={() => navigate('/buyer/products')}
                className="inline-flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium transition-colors"
              >
                <ShoppingCart className="h-4 w-4 mr-2" />
                Browse Products
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Orders Count */}
            <div className="mb-4 text-sm text-gray-600">
              Showing {filteredOrders.length} of {orders.length} orders
            </div>
            
            {/* Orders Grid */}
            <div className="space-y-4">
              {filteredOrders.map((order) => {
                const StatusIcon = statusConfig[order.orderStatus]?.icon || AlertCircle;
                const statusColor = statusConfig[order.orderStatus]?.color || 'bg-gray-100 text-gray-800';
                
                return (
                  <div key={order.id} className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-lg transition-shadow overflow-hidden">
                    {/* Order Header */}
                    <div 
                      className="p-6 cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={() => toggleOrderDetails(order.id)}
                    >
                      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-start gap-4">
                            {order.productImage ? (
                              <img
                                src={order.productImage}
                                alt={order.productName}
                                className="w-16 h-16 rounded-lg object-cover border border-gray-200"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = "https://via.placeholder.com/64x64?text=No+Image";
                                }}
                              />
                            ) : (
                              <div className="w-16 h-16 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center">
                                <Package className="h-8 w-8 text-gray-400" />
                              </div>
                            )}
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <h3 className="font-semibold text-gray-900 text-lg">
                                    {order.productName || 'Unnamed Product'}
                                  </h3>
                                  
                                  {/* NEW: Show selected color badge in header if exists */}
                                  {order.selectedColor && (
                                    <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full font-medium flex items-center">
                                      <Palette className="h-3 w-3 mr-1" />
                                      {order.selectedColor}
                                    </span>
                                  )}
                                </div>
                                <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center ${statusColor}`}>
                                  <StatusIcon className="h-3 w-3 mr-1.5" />
                                  {statusConfig[order.orderStatus]?.label || order.orderStatus || 'Unknown'}
                                </span>
                              </div>
                              
                              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm text-gray-600">
                                <div className="flex items-center">
                                  <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                                  <span>{formatDate(order.createdAt)}</span>
                                </div>
                                <div className="flex items-center">
                                  <Hash className="h-4 w-4 mr-2 text-gray-400" />
                                  <span className="font-mono bg-gray-100 px-2 py-1 rounded text-xs">
                                    ID: {order.orderId?.substring(0, 8) || order.id?.substring(0, 8) || 'N/A'}
                                  </span>
                                </div>
                                <div className="flex items-center">
                                  <Building className="h-4 w-4 mr-2 text-gray-400" />
                                  <span className="truncate" title={order.sellerName}>
                                    {order.sellerName || 'Unknown Seller'}
                                  </span>
                                </div>
                                <div className="flex items-center">
                                  <IndianRupee className="h-4 w-4 mr-2 text-gray-400" />
                                  <span className="font-medium">
                                    {formatINR(order.totalAmount || 0)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="flex flex-col items-end">
                          <ChevronRight className={`h-5 w-5 text-gray-400 transition-transform ${expandedOrder === order.id ? 'rotate-90' : ''}`} />
                        </div>
                      </div>
                    </div>

                    {/* Order Details (Expanded) */}
                    {expandedOrder === order.id && (
                      <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                          {/* Order Details */}
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                              <FileText className="h-5 w-5 mr-2 text-orange-600" />
                              Order Details
                            </h4>
                            <div className="bg-white p-4 rounded-lg border border-gray-200">
                              <div className="space-y-3">
                                <div className="flex justify-between items-center">
                                  <span className="text-gray-600">Product:</span>
                                  <span className="font-medium text-right max-w-[200px]">{order.productName || 'N/A'}</span>
                                </div>
                                
                                {/* NEW: Display Selected Color */}
                                {order.selectedColor && (
                                  <div className="flex justify-between items-center">
                                    <span className="text-gray-600 flex items-center">
                                      <Palette className="h-4 w-4 mr-2 text-gray-400" />
                                      Selected Color:
                                    </span>
                                    <span className="font-medium px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm flex items-center">
                                      <Palette className="h-3 w-3 mr-1.5" />
                                      {order.selectedColor}
                                    </span>
                                  </div>
                                )}
                                
                                {/* NEW: Display Available Colors */}
                                {order.productColors && order.productColors.trim() !== '' && (
                                  <div className="flex justify-between items-start">
                                    <span className="text-gray-600 flex items-start pt-1">
                                      <Palette className="h-4 w-4 mr-2 text-gray-400 flex-shrink-0 mt-0.5" />
                                      Available Colors:
                                    </span>
                                    <div className="text-right">
                                      <span className="font-medium text-sm mb-1 block">
                                        {order.productColors}
                                      </span>
                                      {order.availableColors && order.availableColors.length > 0 && (
                                        <div className="flex flex-wrap gap-1.5 justify-end mt-1">
                                          {order.availableColors.map((color, idx) => (
                                            <span 
                                              key={idx} 
                                              className={`px-2 py-0.5 text-xs rounded-full ${order.selectedColor === color
                                                ? 'bg-orange-100 text-orange-800 border border-orange-300'
                                                : 'bg-gray-100 text-gray-700'
                                              }`}
                                            >
                                              {color}
                                            </span>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                                
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Quantity:</span>
                                  <span className="font-medium">{order.quantity || 0} {order.productUnit || 'units'}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Unit Price:</span>
                                  <span className="font-medium">{formatINR(order.unitPrice || 0)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">MOQ:</span>
                                  <span className="font-medium">{order.moq || 1} {order.productUnit || 'units'}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Total Amount:</span>
                                  <span className="font-bold text-orange-600">{formatINR(order.totalAmount || 0)}</span>
                                </div>
                                {order.category && (
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Category:</span>
                                    <span className="font-medium">{order.category}</span>
                                  </div>
                                )}
                                {order.material && (
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Material:</span>
                                    <span className="font-medium">{order.material}</span>
                                  </div>
                                )}
                                {order.leadTime && (
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Lead Time:</span>
                                    <span className="font-medium">{order.leadTime}</span>
                                  </div>
                                )}
                                {order.paymentTerms && (
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Payment Terms:</span>
                                    <span className="font-medium">{order.paymentTerms}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Seller Information */}
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                              <Building className="h-5 w-5 mr-2 text-orange-600" />
                              Seller Information
                            </h4>
                            <div className="space-y-3">
                              <div className="bg-white p-4 rounded-lg border border-gray-200">
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex items-center">
                                    <Building className="h-5 w-5 mr-2 text-gray-500" />
                                    <span className="font-medium">Seller Details</span>
                                  </div>
                                  {order.sellerVerified && (
                                    <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full flex items-center">
                                      <BadgeCheck className="h-3 w-3 mr-1" />
                                      Verified
                                    </span>
                                  )}
                                </div>
                                <div className="space-y-2.5">
                                  <div className="flex justify-between items-center">
                                    <span className="text-gray-600">Seller:</span>
                                    <span className="font-medium text-right max-w-[200px]">{order.sellerName || 'N/A'}</span>
                                  </div>
                                  
                                  {order.sellerContactPerson && order.sellerContactPerson !== order.sellerName && (
                                    <div className="flex justify-between items-center">
                                      <span className="text-gray-600">Contact Person:</span>
                                      <span className="font-medium">{order.sellerContactPerson}</span>
                                    </div>
                                  )}
                                  
                                  {order.sellerCompany && (
                                    <div className="flex justify-between items-center">
                                      <span className="text-gray-600">Company:</span>
                                      <span className="font-medium">{order.sellerCompany}</span>
                                    </div>
                                  )}
                                  
                                  {(order.sellerCity || order.sellerState) && (
                                    <div className="flex justify-between items-center">
                                      <span className="text-gray-600">Location:</span>
                                      <div className="flex items-center gap-2">
                                        <MapPin className="h-3 w-3 text-gray-400" />
                                        <span className="font-medium">
                                          {order.sellerCity || ''}{order.sellerCity && order.sellerState ? ', ' : ''}{order.sellerState || ''}
                                        </span>
                                      </div>
                                    </div>
                                  )}
                                  
                                  {order.sellerWebsite && (
                                    <div className="flex justify-between items-center">
                                      <span className="text-gray-600">Website:</span>
                                      <div className="flex items-center gap-2">
                                        <a 
                                          href={order.sellerWebsite.startsWith('http') ? order.sellerWebsite : `https://${order.sellerWebsite}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="font-medium text-blue-600 hover:text-blue-800 truncate max-w-[150px]"
                                        >
                                          {order.sellerWebsite.replace(/^https?:\/\//, '')}
                                        </a>
                                      </div>
                                    </div>
                                  )}
                                  
                                  <div className="flex justify-between items-center">
                                    <span className="text-gray-600">Seller ID:</span>
                                    <span className="font-medium text-xs font-mono bg-gray-100 px-2 py-1 rounded">
                                      {order.sellerId?.substring(0, 8) || 'N/A'}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              <div className="bg-white p-4 rounded-lg border border-gray-200">
                                <div className="flex items-center mb-3">
                                  <Briefcase className="h-5 w-5 mr-2 text-gray-500" />
                                  <span className="font-medium">Order Status</span>
                                </div>
                                <div className="space-y-2">
                                  <div className="flex justify-between items-center">
                                    <span className="text-gray-600">Status:</span>
                                    <span className={`font-medium ${statusColor} px-3 py-1 rounded-full text-xs flex items-center`}>
                                      <StatusIcon className="h-3 w-3 mr-1.5" />
                                      {statusConfig[order.orderStatus]?.label || order.orderStatus || 'Unknown'}
                                    </span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Payment Status:</span>
                                    <span className={`font-medium ${order.paymentStatus === 'paid' ? 'text-green-600' : 'text-yellow-600'}`}>
                                      {(order.paymentStatus || 'pending').toUpperCase()}
                                    </span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Order Type:</span>
                                    <span className="font-medium">{order.orderType || 'direct_order'}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Order Date:</span>
                                    <span className="font-medium">{formatDate(order.createdAt)}</span>
                                  </div>
                                  {order.sellerResponseTime && (
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Response Time:</span>
                                      <span className="font-medium text-green-600 flex items-center">
                                        <Clock className="h-3 w-3 mr-1" />
                                        {order.sellerResponseTime}
                                      </span>
                                    </div>
                                  )}
                                  {order.sellerResponseRate && (
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Response Rate:</span>
                                      <span className="font-medium text-green-600">{order.sellerResponseRate}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Color Information Section - NEW */}
                        {(order.selectedColor || order.productColors) && (
                          <div className="mt-6 bg-white rounded-lg border border-gray-200 p-4">
                            <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                              <Palette className="h-5 w-5 mr-2 text-orange-600" />
                              Color Information
                            </h4>
                            <div className="space-y-3">
                              {order.selectedColor && (
                                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-100">
                                  <div className="flex items-center">
                                    <Palette className="h-4 w-4 mr-3 text-blue-600" />
                                    <div>
                                      <div className="font-medium text-blue-800">Selected Color</div>
                                      <div className="text-sm text-blue-700">This is the color you selected when placing the order</div>
                                    </div>
                                  </div>
                                  <span className="px-3 py-1 bg-blue-600 text-white rounded-full font-medium text-sm">
                                    {order.selectedColor}
                                  </span>
                                </div>
                              )}
                              
                              {order.productColors && order.productColors.trim() !== '' && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                                    <div className="text-sm font-medium text-gray-700 mb-1">All Available Colors</div>
                                    <div className="text-gray-600">{order.productColors}</div>
                                  </div>
                                  
                                  {order.availableColors && order.availableColors.length > 0 && (
                                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                                      <div className="text-sm font-medium text-gray-700 mb-2">Individual Colors</div>
                                      <div className="flex flex-wrap gap-1.5">
                                        {order.availableColors.map((color, idx) => (
                                          <div 
                                            key={idx} 
                                            className={`px-2 py-1 text-xs rounded-full flex items-center ${order.selectedColor === color
                                              ? 'bg-orange-100 text-orange-800 border border-orange-300 font-medium'
                                              : 'bg-gray-200 text-gray-700'
                                            }`}
                                          >
                                            {order.selectedColor === color && (
                                              <CheckCircle className="h-3 w-3 mr-1" />
                                            )}
                                            {color}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Action Buttons */}
                        <div className="flex flex-wrap gap-2 mt-6 pt-4 border-t border-gray-200">
                          {/* View Product */}
                          {order.productId && (
                            <button 
                              onClick={() => navigate(`/buyer/products/${order.productId}`)}
                              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center text-sm font-medium transition-colors"
                              title="View Product Details"
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              View Product
                            </button>
                          )}
                          
                          {/* Cancel Order */}
                          {(order.orderStatus === 'pending' || order.orderStatus === 'confirmed') && (
                            <button
                              onClick={() => handleOrderAction(order.id, 'cancel')}
                              className="px-4 py-2 border border-red-200 text-red-600 rounded-lg hover:bg-red-50 flex items-center text-sm font-medium transition-colors"
                              title="Cancel Order"
                            >
                              <XCircle className="h-4 w-4 mr-2" />
                              Cancel Order
                            </button>
                          )}
                        </div>

                        {/* View Product Notice */}
                        {order.productId && (
                          <div className="mt-4 p-4 rounded-lg border bg-blue-50 border-blue-200">
                            <div className="flex items-start">
                              <AlertCircle className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                              <div>
                                <p className="text-sm text-blue-800 font-medium mb-1">
                                  Need to contact the seller?
                                </p>
                                <p className="text-sm text-blue-700">
                                  Visit the product page to find seller contact information and communicate directly for order updates.
                                </p>
                                <button
                                  onClick={() => navigate(`/buyer/products/${order.productId}`)}
                                  className="text-xs text-orange-600 hover:text-orange-700 mt-2 flex items-center"
                                >
                                  <ExternalLink className="h-3 w-3 mr-1" />
                                  Go to Product Page
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MyOrdersPage;  